/**
 * Auth composable
 *
 * Provides authentication logic and user session management
 */

import { computed, nextTick, hasInjectionContext } from 'vue'
import {useQueryClient} from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import { useAuthStore, type AuthUser } from '@/stores/auth'
import { authAPI, type LoginResponse, type LoginOtpRequest, type LoginOtpVerifyRequest, type RegisterOtpResendRequest, type RegisterOtpVerifyRequest, type RegisterRequest, type UserResponse } from '@/api/auth'

export const normalizeAuthUser = (response: LoginResponse | UserResponse): AuthUser => {
  const source: any = 'user' in response ? response.user : response

  if (!source.id || !source.email) {
    throw new Error('Login response did not include user')
  }

  const firstName = source.firstName || ''
  const lastName = source.lastName || ''
  const fallbackName = `${firstName} ${lastName}`.trim()

  return {
    id: source.id,
    email: source.email,
    name: source.name || fallbackName || source.email,
    role: source.role,
    details: source,
  }
}

export function useAuth() {
  const authStore = useAuthStore()
  const router = useRouter()
  const queryClient = hasInjectionContext() ? useQueryClient() : undefined

  const isAuthenticated = computed(() => authStore.isAuthenticated)
  const user = computed(() => authStore.user)

  const acceptSession = (response: LoginResponse) => {
    authStore.setSession(response.token, normalizeAuthUser(response))
  }

  const login = async (email: string, password: string) => {
    const response = await authAPI.login({ email, password })
    authStore.setSession(response.token, normalizeAuthUser(response))
  }

  const requestLoginOtp = async (request: LoginOtpRequest) => {
    return authAPI.requestLoginOtp(request)
  }

  const verifyLoginOtp = async (request: LoginOtpVerifyRequest) => {
    const response = await authAPI.verifyLoginOtp(request)
    authStore.setSession(response.token, normalizeAuthUser(response))
    return response
  }

  const register = async (request: RegisterRequest) => {
    return authAPI.register(request)
  }

  const verifyRegistrationOtp = async (request: RegisterOtpVerifyRequest) => {
    const response = await authAPI.verifyRegistrationOtp(request)
    authStore.setSession(response.token, normalizeAuthUser(response))
    return response
  }

  const resendRegistrationOtp = async (request: RegisterOtpResendRequest) => {
    return authAPI.resendRegistrationOtp(request)
  }

  const searchMasqueradeTargets = async (query: string, limit = 8) => {
    const response = await authAPI.searchMasqueradeTargets(query, limit)
    return response.users
  }

  const startMasquerade = async (
    password: string,
    targetUserId: string,
    targetEmail: string
  ) => {
    const originalToken = authStore.token
    const location = router.currentRoute.value
    const response = await authAPI.startMasquerade({
      password,
      targetUserId,
      targetEmail,
    })
    if (authStore.token !== originalToken) return
    const target = normalizeAuthUser(response)
    authStore.switchingIdentity = true
    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), 8000)
    try {
      // Remove the departing staff page before publishing the new session, so
      // its reactive queries cannot restart under the athlete's credentials.
      await nextTick()
      await queryClient?.cancelQueries()
      if (authStore.token !== originalToken) return
      authStore.beginMasquerade(response.token, target, location.fullPath)
      let athleteHome = ''
      const [{canKeepMasqueradeLocation}, {tryoutHomeQueryKey}, {eventViewAccess}] = await Promise.all([
        import('./masqueradeLocation'),
        import('@/features/tryout-home/composables'),
        import('@/stores/eventView'),
      ])
      if (authStore.token !== response.token) return
      const keep = await canKeepMasqueradeLocation(location, target, home => {
        if (authStore.token !== response.token) return
        const club = String(location.params.clubId), season = String(location.params.seasonId), event = String(location.params.tryoutId)
        queryClient?.setQueryData([...tryoutHomeQueryKey(season,event,club), target.id],home)
        const access = eventViewAccess(home.event.roles)
        if (access.athlete && !access.staff) athleteHome = `/clubhouse/${club}/season/${season}/tryout/${event}?view=athlete`
      }, controller.signal)
      if (authStore.token === response.token && router.currentRoute.value.fullPath === location.fullPath) {
        const destination = athleteHome || (keep ? location.fullPath : '/')
        if (destination !== location.fullPath) await router.replace(destination)
      }
    } finally {
      window.clearTimeout(timeout)
      authStore.switchingIdentity = false
    }
  }

  const stopMasquerade = async () => {
    if (!authStore.isMasquerading || authStore.switchingIdentity) return
    const destination = authStore.masqueradeReturnPath
    authStore.switchingIdentity = true
    try {
      await nextTick()
      await queryClient?.cancelQueries()
      if (authStore.stopMasquerade()) await router.replace(destination)
    } finally {
      authStore.switchingIdentity = false
    }
  }

  const requestPasswordReset = async (email: string) => {
    await authAPI.requestPasswordReset({ email })
  }

  const verifyPasswordToken = async (token: string) => {
    return authAPI.verifyPasswordToken(token)
  }

  const requestNewPasswordToken = async (token: string) => {
    await authAPI.requestNewPasswordToken(token)
  }

  const resetPassword = async (
    token: string,
    password: string,
    confirmPassword: string
  ) => {
    return authAPI.resetPassword(token, { password, confirmPassword })
  }

  const logout = async () => {
    const departingToken = authStore.token
    // Lock all local account content immediately, including without a network.
    authStore.logout()
    router.push('/login')
    if (departingToken) {
      try { await authAPI.logout(undefined, departingToken) } catch { /* Local logout is already complete. */ }
    }
  }

  const getCurrentUser = async () => {
    if (!authStore.token) {
      return null
    }
    try {
      const userResponse = await authAPI.getCurrentUser()
      authStore.setUser(normalizeAuthUser(userResponse))
      return userResponse
    } catch {
      authStore.logout()
      return null
    }
  }

  return {
    isAuthenticated,
    user,
    acceptSession,
    login,
    requestLoginOtp,
    verifyLoginOtp,
    register,
    verifyRegistrationOtp,
    resendRegistrationOtp,
    searchMasqueradeTargets,
    startMasquerade,
    stopMasquerade,
    requestPasswordReset,
    verifyPasswordToken,
    requestNewPasswordToken,
    resetPassword,
    logout,
    getCurrentUser,
  }
}
